import React, { useState } from "react";
import styles from "./BlogSecLeft.module.css";
import FaqSection from "@/Component/Common/FaqSection";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import { saveBlogComment } from "@/services/api/blog";
import {
  buildBlogCommentPayload,
  createBlogCommentFormState,
  getBlogApiErrorMessage,
  isBlogApiSuccess,
  validateBlogCommentForm,
} from "@/utils/blogFormHelpers";
import { mapApiFaqs } from "@/utils/dealsHelpers";

export default function BlogSecLeft({ postData }) {
  const { showAlert } = useAlert();

  const title = postData?.title || "";
  const contentSections = postData?.sections || [];
  const htmlContent = postData?.htmlContent || "";
  const faqs = mapApiFaqs(postData?.faqs) || [];
  const blogId = postData?.id || "";

  const [commentForm, setCommentForm] = useState(createBlogCommentFormState);
  const [commentErrors, setCommentErrors] = useState({});
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [totalComments, setTotalComments] = useState(
    () => Number(postData?.totalComments) || 0
  );

  const updateCommentField = (field, value) => {
    setCommentForm((prev) => ({ ...prev, [field]: value }));
    setCommentErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    const errors = validateBlogCommentForm(commentForm);
    if (Object.keys(errors).length > 0) {
      setCommentErrors(errors);
      return;
    }

    setIsSubmittingComment(true);
    try {
      const response = await saveBlogComment(
        buildBlogCommentPayload(commentForm, { blogId })
      );

      if (!isBlogApiSuccess(response)) {
        throw new Error(response?.message || "Unable to post comment.");
      }

      showAlert({
        title: "Comment submitted",
        message: response?.message || "Your comment has been submitted successfully.",
      });
      setCommentForm(createBlogCommentFormState());
      setCommentErrors({});
      setTotalComments((prev) => prev + 1);
    } catch (error) {
      showAlert({
        title: "Comment failed",
        message: getBlogApiErrorMessage(error, "Unable to post comment."),
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };
console.log(htmlContent, contentSections);

  return (
    <div className={styles.blogSecLeft}>
      <h1 className="standard-heading">{title}</h1>

      <div className={styles.articleBody}>
        {htmlContent ? (
          <div
            className={styles.articleHtml}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : contentSections.length > 0 ? (
          contentSections.map((sec, idx) => (
            <div key={idx} className={styles.sectionBlock}>
              {sec.heading && (
                <h2 className="standard-heading">{sec.heading}</h2>
              )}
              {sec.description && (
                <p className={styles.paragraph}>{sec.description}</p>
              )}

              {sec.images && sec.images.length > 0 && (
                <div className={styles.imageRow}>
                  {(sec.images && sec.images.length > 0
                    ? sec.images
                    : ["/images/blog-inner-1.jpg", "/images/blog-inner-2.jpg"]
                  ).map((imgUrl, imgIdx) => (
                    <img
                      key={imgIdx}
                      src={imgUrl}
                      alt={sec.heading || "blog-image"}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/600x400?text=Blog+Image";
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="nodata">No content available.</p>
        )}
      </div>
      {faqs.length > 0 ? <FaqSection items={faqs} className={styles.faqOverride}/> : null}

      <div className={styles.commentSection}>
        <h2 className={styles.sectionTitle}>Comments</h2>
        <form onSubmit={handleCommentSubmit} className={styles.commentForm} noValidate>
          <div className={styles.formField}>
            <fieldset
              className={`${styles.inputGroup} ${
                commentErrors.comment ? styles.inputGroupError : ""
              }`.trim()}
            >
              <legend className={styles.inputLegend}>What are your thoughts?</legend>
              <textarea
                placeholder="Type here..."
                rows={3}
                value={commentForm.comment}
                onChange={(event) =>
                  updateCommentField("comment", event.target.value)
                }
                className={styles.commentTextarea}
                aria-invalid={Boolean(commentErrors.comment)}
              />
            </fieldset>
            {commentErrors.comment ? (
              <span className={styles.fieldError} role="alert">
                {commentErrors.comment}
              </span>
            ) : null}
          </div>

          <div className={styles.inputRow}>
            <div className={styles.formField}>
              <fieldset
                className={`${styles.inputGroup} ${
                  commentErrors.userName ? styles.inputGroupError : ""
                }`.trim()}
              >
                <legend className={styles.inputLegend}>Full name*</legend>
                <input
                  type="text"
                  placeholder="Your Full Name"
                  value={commentForm.userName}
                  onChange={(event) =>
                    updateCommentField("userName", event.target.value)
                  }
                  className={styles.commentInput}
                  aria-invalid={Boolean(commentErrors.userName)}
                />
              </fieldset>
              {commentErrors.userName ? (
                <span className={styles.fieldError} role="alert">
                  {commentErrors.userName}
                </span>
              ) : null}
            </div>

            <div className={styles.formField}>
              <fieldset
                className={`${styles.inputGroup} ${
                  commentErrors.emailId ? styles.inputGroupError : ""
                }`.trim()}
              >
                <legend className={styles.inputLegend}>Email*</legend>
                <input
                  type="email"
                  placeholder="Your Email Address"
                  value={commentForm.emailId}
                  onChange={(event) =>
                    updateCommentField("emailId", event.target.value)
                  }
                  className={styles.commentInput}
                  aria-invalid={Boolean(commentErrors.emailId)}
                />
              </fieldset>
              {commentErrors.emailId ? (
                <span className={styles.fieldError} role="alert">
                  {commentErrors.emailId}
                </span>
              ) : null}
            </div>
          </div>

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isSubmittingComment}
          >
            {isSubmittingComment ? "Submitting..." : "Add Your Comment"}
          </button>
        </form>
      </div>
    </div>
  );
}
